import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, TextArea, FormField, Alert } from '../ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface BusinessFormProps {
  isOpen: boolean;
  onClose: () => void;
  onBusinessCreated: () => void; // Callback for success
  // Add initialBusinessData and isEditMode later for editing
}

const initialFormData = {
    name: '',
    description: '',
};

const BusinessForm: React.FC<BusinessFormProps> = ({
    isOpen,
    onClose,
    onBusinessCreated
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
        // Reset to initial state when opening for creation
        setFormData(initialFormData);
    }
    // Clear errors/success on close regardless of mode
    if (!isOpen) {
        setTimeout(() => { // Delay to allow fade-out
           setSubmitError(null);
           setSubmitSuccess(false);
        }, 300)
    }

  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const response = await apiClient.post('/api/businesses/', formData);

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        onBusinessCreated();
        setFormData(initialFormData); // Clear form
        setTimeout(() => { onClose(); }, 1500);
      } else {
        throw new Error(`Failed to create business (${response.status})`);
      }
    } catch (err: any) {
      console.error(`Error creating business:`, err);
      const errorMsg = err.message || `Failed to create business.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
      if (!isSubmitting) {
          onClose();
      }
  }, [isSubmitting, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child /* Backdrop */
            as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0"
            enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child /* Panel */
                as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                     Create New Business
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      opacity: isSubmitting ? 0.5 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                    }}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  {/* Name */}
                  <FormField label="Name" htmlFor="name" required>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </FormField>

                  {/* Description */}
                  <FormField label="Description (Optional)" htmlFor="description">
                    <TextArea
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </FormField>

                  {/* Submit/Cancel Area */}
                  <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                        {submitSuccess && (
                            <Alert variant="success">
                                Business created!
                            </Alert>
                        )}
                        {submitError && (
                            <Alert variant="error">
                                {submitError}
                            </Alert>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[12] }}>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            isLoading={isSubmitting}
                            loadingText="Creating..."
                            disabled={isSubmitting}
                          >
                            Create Business
                          </Button>
                        </div>
                    </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BusinessForm;
